import type { SignInPresentationBody } from '@ayumia/contracts';
import { cssVariable } from '../theme/theme';
import type { SignInLocale } from './signin-i18n';

/**
 * Habillage de la page de connexion (ADR-092 §7) : lu **avant** authentification sur
 * `GET /api/v1/public/sign-in-presentation`, sans session. Toute option est appliquée ; une
 * réponse absente, en erreur ou mal formée retombe sur la copie locale du défaut de plateforme
 * ci-dessous (look v1) : la page ne casse jamais. Rien ici ne désigne un tenant.
 */

export interface SignInBackground {
  kind: 'image' | 'color' | 'none';
  image?: string;
  color_ref?: string;
}

/** Copie locale du défaut de plateforme servi par l'API. */
export const DEFAULT_SIGN_IN_PRESENTATION: SignInPresentationBody = {
  background: { kind: 'image', image: '/brand/sign-in-background.webp' },
  logo: '/brand/ayumia-logo.png',
  title: { fr: 'Connectez-vous pour continuer.', en: 'Sign in to continue.', es: 'Inicie sesión para continuar.' },
  locales: ['en', 'fr', 'es'],
  default_locale: 'fr',
  show_locale_switcher: true,
  remember_me: true,
  forgot_password: true,
  booking_link: { enabled: true },
};

const LOCALES = new Set<SignInLocale>(['fr', 'en', 'es']);
/** Actif public de la marque, servi par le front : jamais une URL externe. */
const BRAND_ASSET = /^\/brand\/[A-Za-z0-9._-]+$/;
const TOKEN_PATH = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;

export function brandAsset(path: string | undefined): string | undefined {
  return path !== undefined && BRAND_ASSET.test(path) && !path.includes('..') ? path : undefined;
}

/** Fond de page : variable CSS d'une image de marque, ou d'un jeton de couleur ; jamais une valeur libre. */
export function backgroundStyle(background: SignInBackground | undefined): Record<string, string> {
  if (background?.kind === 'image') {
    const image = brandAsset(background.image);
    return image === undefined ? {} : { '--ay-signin-illustration': `url("${image}")` };
  }
  if (background?.kind === 'color' && background.color_ref !== undefined && TOKEN_PATH.test(background.color_ref)) {
    return { '--ay-signin-background': `var(${cssVariable(background.color_ref)})` };
  }
  return {};
}

export function backgroundOf(presentation: SignInPresentationBody): SignInBackground {
  return presentation.background as unknown as SignInBackground;
}

/** Corps reçu mal formé : on garde le défaut, champ par champ quand c'est possible. */
export function normalizePresentation(raw: unknown): SignInPresentationBody {
  const body = (raw !== null && typeof raw === 'object' && 'data' in raw ? (raw as { data: unknown }).data : raw) as Partial<SignInPresentationBody> | null;
  if (body === null || typeof body !== 'object' || typeof body.title !== 'object' || body.title === null) return DEFAULT_SIGN_IN_PRESENTATION;
  const locales = (Array.isArray(body.locales) ? body.locales : []).filter((locale): locale is SignInLocale => LOCALES.has(locale as SignInLocale));
  const merged: SignInPresentationBody = {
    ...DEFAULT_SIGN_IN_PRESENTATION,
    ...body,
    locales: locales.length > 0 ? (locales as SignInPresentationBody['locales']) : DEFAULT_SIGN_IN_PRESENTATION.locales,
  };
  if (!merged.locales.includes(merged.default_locale)) merged.default_locale = merged.locales[0];
  return merged;
}

/** Lecture par le client d'authentification ; tout échec rend le défaut de plateforme. */
export async function fetchSignInPresentation(client: { signInPresentation(): Promise<unknown> }): Promise<SignInPresentationBody> {
  try {
    return normalizePresentation(await client.signInPresentation());
  } catch {
    return DEFAULT_SIGN_IN_PRESENTATION;
  }
}
